/**
 * Threshold Badge Component
 * Displays a color-coded badge indicating token threshold status
 */
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type { ThresholdStatusValue } from '@/types/pricing'

export interface ThresholdBadgeProps {
  /** The threshold status to display */
  status: ThresholdStatusValue | 'none'
  /** Current token count (for tooltip) */
  current?: number
  /** Warning threshold value (for tooltip) */
  warningThreshold?: number
  /** Critical threshold value (for tooltip) */
  criticalThreshold?: number
  /** Label for the type (e.g., "Input" or "Output") */
  label?: string
  /** Additional CSS classes */
  className?: string
}

/**
 * Get display properties for a threshold status
 */
function getStatusDisplay(status: ThresholdStatusValue | 'none'): {
  emoji: string
  label: string
  colorClass: string
} {
  switch (status) {
    case 'green':
      return {
        emoji: '🟢',
        label: 'Normal',
        colorClass: 'text-green-600',
      }
    case 'yellow':
      return {
        emoji: '🟡',
        label: 'Warning',
        colorClass: 'text-yellow-600',
      }
    case 'red':
      return {
        emoji: '🔴',
        label: 'Critical',
        colorClass: 'text-red-600',
      }
    case 'none':
    default:
      return {
        emoji: '-',
        label: 'No threshold configured',
        colorClass: 'text-muted-foreground',
      }
  }
}

/**
 * Format large numbers for display
 */
function formatNumber(value: number | undefined): string {
  if (value === undefined) return '-'
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`
  }
  return value.toLocaleString()
}

/**
 * ThresholdBadge displays a visual indicator of token threshold status
 * with an optional tooltip showing current usage vs thresholds
 */
export function ThresholdBadge({
  status,
  current,
  warningThreshold,
  criticalThreshold,
  label,
  className,
}: ThresholdBadgeProps) {
  const display = getStatusDisplay(status)

  // If no tooltip data, just show the badge
  if (current === undefined && warningThreshold === undefined && criticalThreshold === undefined) {
    return (
      <span
        className={cn('text-base', display.colorClass, className)}
        aria-label={display.label}
      >
        {display.emoji}
      </span>
    )
  }

  // Show badge with tooltip
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn('cursor-help text-base', display.colorClass, className)}
          aria-label={display.label}
        >
          {display.emoji}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs">
        <div className="space-y-1 text-xs">
          {label && <div className="font-medium">{label} Tokens</div>}
          <div className="space-y-0.5">
            {current !== undefined && (
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Current:</span>
                <span className="font-mono">{formatNumber(current)}</span>
              </div>
            )}
            {warningThreshold !== undefined && (
              <div className="flex justify-between gap-4">
                <span className="text-yellow-600">Warning:</span>
                <span className="font-mono">{formatNumber(warningThreshold)}</span>
              </div>
            )}
            {criticalThreshold !== undefined && (
              <div className="flex justify-between gap-4">
                <span className="text-red-600">Critical:</span>
                <span className="font-mono">{formatNumber(criticalThreshold)}</span>
              </div>
            )}
          </div>
          <div className="pt-1 text-muted-foreground">{display.label}</div>
        </div>
      </TooltipContent>
    </Tooltip>
  )
}
